// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { PractitionerRole } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';

export interface UseAllPractitionerRolesResult {
  practitionerRoles: PractitionerRole[];
  loading: boolean;
  error: string | undefined;
}

export function useAllPractitionerRoles(): UseAllPractitionerRolesResult {
  const medplum = useMedplum();
  const [practitionerRoles, setPractitionerRoles] = useState<PractitionerRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(undefined);
    // _count: 1000 is a safe upper bound for current org size (~200 roles).
    // If it is ever exceeded, switch to a Medplum aggregate (e.g. $count operation).
    medplum
      .searchResources('PractitionerRole', { _count: '1000' }, { cache: 'no-cache' })
      .then((roles) => {
        if (!active) {
          return;
        }
        setPractitionerRoles(roles);
      })
      .catch((err) => {
        if (!active) {
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [medplum]);

  return { practitionerRoles, loading, error };
}
